import { dbm } from "./config";

/**
 * Listings from the R-Property manager dashboard, flattened for browsing — a
 * firebase-v8-compat port of the app's Database/managedProperties.js. A manager files
 * each building under their own subcollection, so there is no single "properties"
 * collection: every listing is fetched by walking the managers and pulling each one's
 * properties, and the manager's contact card is stitched onto every row on the way
 * through.
 */

export const MANAGERS_COLLECTION = "Users";

const asText = (value) => (typeof value === "string" ? value.trim() : "");

// units and rentAmount are written by <input type="number">, so they arrive as strings
// ("12", "25000") and are '' whenever the manager left the field blank.
const asNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// createdAt is a Firestore Timestamp, except on a document the server has not confirmed
// yet, where it reads back as null.
const asMillis = (value) => (typeof value?.toMillis === "function" ? value.toMillis() : null);

const shapeManager = (id, data = {}) => {
  const person = asText(data.name) || "Property manager";
  const company = asText(data.company);

  return {
    id,
    // The trading name is what a client recognises, so it leads; the person's name is
    // kept for the "managed by" line.
    name: company || person,
    contactName: person,
    company,
    phone: asText(data.phone),
    email: asText(data.email),
    county: asText(data.county),
    constituency: asText(data.constituency),
    area: [asText(data.constituency), asText(data.county)].filter(Boolean).join(", "),
  };
};

export const LISTING_TYPES = { RENT: "For Rent", SALE: "For Sale" };

// Older properties were saved before the manager form had a listing type; treating the
// blank as a rental matches what the dashboard was originally built for.
const asListingType = (value) =>
  asText(value) === LISTING_TYPES.SALE ? LISTING_TYPES.SALE : LISTING_TYPES.RENT;

// Photos and videos the manager uploaded: [{ url, path, name, type: 'image' | 'video' }].
// Older properties were saved before media existed and simply have none.
const shapeMedia = (media) => {
  if (!Array.isArray(media)) return [];

  return media
    .filter((item) => typeof item?.url === "string" && item.url)
    .map((item) => ({
      url: item.url,
      type: item.type === "video" ? "video" : "image",
    }));
};

// A pin dropped through the dashboard's LocationPicker — null for properties saved
// before that existed, or where the manager hasn't re-saved through the new form yet.
const asLocation = (value) =>
  value && typeof value.latitude === "number" && typeof value.longitude === "number"
    ? { latitude: value.latitude, longitude: value.longitude }
    : null;

const shapeProperty = (id, data = {}, manager) => {
  const media = shapeMedia(data.media);

  return {
    id,
    managerId: manager.id,
    name: asText(data.name) || "Untitled property",
    address: asText(data.address),
    location: asLocation(data.location),
    description: asText(data.description),
    type: asText(data.type) || "unspecified",
    units: asNumber(data.units),
    rentAmount: asNumber(data.rentAmount),
    createdAtMillis: asMillis(data.createdAt),
    listingType: asListingType(data.listingType),
    media,
    // The card needs a still to show, and a video carries none — so the cover is the
    // first image if there is one, and null for a video-only listing.
    cover: media.find((item) => item.type === "image") ?? null,
    videoCount: media.filter((item) => item.type === "video").length,
    area: manager.area,
    manager,
  };
};

/**
 * Every listing from every manager, newest first. Deliberately unsorted server-side:
 * hiding a listing is worse than showing it in the wrong order.
 */
export const fetchManagedProperties = async () => {
  const managersSnapshot = await dbm.collection(MANAGERS_COLLECTION).get();

  const perManager = await Promise.all(
    managersSnapshot.docs.map(async (managerDoc) => {
      const manager = shapeManager(managerDoc.id, managerDoc.data());

      try {
        const snapshot = await dbm
          .collection(MANAGERS_COLLECTION)
          .doc(managerDoc.id)
          .collection("properties")
          .get();

        // Marking a property "occupied" in the dashboard takes it off this
        // listing — there's nothing for a house hunter to act on. Missing
        // status (older properties) defaults to shown, same as "vacant".
        return snapshot.docs
          .filter((entry) => entry.data().status !== "occupied")
          .map((entry) => shapeProperty(entry.id, entry.data(), manager));
      } catch (e) {
        // One manager's locked-down subcollection must not empty the whole list.
        console.warn(`Could not read properties for ${managerDoc.id}:`, e);
        return [];
      }
    })
  );

  return perManager
    .flat()
    .sort((a, b) => (b.createdAtMillis ?? Infinity) - (a.createdAtMillis ?? Infinity));
};

/**
 * The agencies themselves, each carrying its own listings. Built from the same single
 * pass as fetchManagedProperties so opening the directory costs one read per manager,
 * not two.
 */
export const fetchAgencies = async () => {
  const properties = await fetchManagedProperties();

  const byAgency = new Map();

  properties.forEach((property) => {
    const existing = byAgency.get(property.managerId);

    if (existing) {
      existing.properties.push(property);
    } else {
      byAgency.set(property.managerId, {
        ...property.manager,
        properties: [property],
      });
    }
  });

  return Array.from(byAgency.values()).map((agency) => ({
    ...agency,
    propertyCount: agency.properties.length,
    rentCount: agency.properties.filter((p) => p.listingType === LISTING_TYPES.RENT).length,
    saleCount: agency.properties.filter((p) => p.listingType === LISTING_TYPES.SALE).length,
    cover: agency.properties.find((p) => p.cover)?.cover ?? null,
  }));
};

/** Distinct property types present, for the filter chips. */
export const typesOf = (properties) => {
  const types = new Set(properties.map((property) => property.type).filter(Boolean));
  return Array.from(types).sort((a, b) => a.localeCompare(b));
};

/** The price range covered by a set of listings, for the price filter bounds. */
export const priceRangeOf = (properties) => {
  const prices = properties.map((property) => property.rentAmount).filter((price) => price !== null);

  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

/**
 * Client-side filtering across everything the browse screens expose. `maxPrice`
 * deliberately keeps listings with no price ("Price on request") rather than hiding
 * them — an unpriced listing is not the same as an expensive one.
 */
export const filterManagedProperties = (
  properties,
  { search = "", type = null, listingType = null, minPrice = null, maxPrice = null } = {}
) => {
  const needle = search.trim().toLowerCase();

  return properties.filter((property) => {
    if (listingType && property.listingType !== listingType) return false;
    if (type && property.type !== type) return false;
    if (minPrice !== null && property.rentAmount !== null && property.rentAmount < minPrice) return false;
    if (maxPrice !== null && property.rentAmount !== null && property.rentAmount > maxPrice) return false;
    if (!needle) return true;

    return [property.name, property.address, property.area, property.manager.name, property.manager.company]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(needle));
  });
};

/** Client-side search over the agency directory. */
export const filterAgencies = (agencies, { search = "", county = null } = {}) => {
  const needle = search.trim().toLowerCase();

  return agencies.filter((agency) => {
    if (county && agency.county !== county) return false;
    if (!needle) return true;

    return [agency.name, agency.contactName, agency.area]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(needle));
  });
};

/** Counties present across the directory, for the county filter dropdown. */
export const countiesOf = (agencies) => {
  const counties = new Set(agencies.map((agency) => agency.county).filter(Boolean));
  return Array.from(counties).sort((a, b) => a.localeCompare(b));
};
